import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface AccessibilitySettings {
  reduceMotion: boolean;
  highContrast: boolean;
  largerText: boolean;
  showMotionLabels: boolean;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  loading: boolean;
  updateSetting: (key: keyof AccessibilitySettings, value: boolean) => Promise<void>;
}

const defaultSettings: AccessibilitySettings = {
  reduceMotion: false,
  highContrast: false,
  largerText: false,
  showMotionLabels: true,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  // Apply CSS classes to document root
  const applyAccessibilityClasses = useCallback((currentSettings: AccessibilitySettings) => {
    const html = document.documentElement;
    
    // Reduce motion
    if (currentSettings.reduceMotion) {
      html.classList.add('reduce-motion');
    } else {
      html.classList.remove('reduce-motion');
    }
    
    // High contrast
    if (currentSettings.highContrast) {
      html.classList.add('high-contrast');
    } else {
      html.classList.remove('high-contrast');
    }
    
    // Larger text
    if (currentSettings.largerText) {
      html.classList.add('larger-text');
    } else {
      html.classList.remove('larger-text');
    }
    
    // Show labels
    if (currentSettings.showMotionLabels) {
      html.classList.add('show-labels');
    } else {
      html.classList.remove('show-labels');
    }
  }, []);

  // Load settings from database or fall back to OS preferences
  useEffect(() => {
    const loadSettings = async () => {
      // Check OS preference for reduced motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      if (!user) {
        // Not logged in - use OS preferences only
        const osSettings = {
          ...defaultSettings,
          reduceMotion: prefersReducedMotion,
        };
        setSettings(osSettings);
        applyAccessibilityClasses(osSettings);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('accessibility_preferences')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading accessibility settings:', error);
        }

        if (data) {
          const loadedSettings: AccessibilitySettings = {
            reduceMotion: data.reduce_motion,
            highContrast: data.high_contrast,
            largerText: data.larger_text,
            showMotionLabels: data.show_motion_labels,
          };
          setSettings(loadedSettings);
          applyAccessibilityClasses(loadedSettings);
        } else {
          // No saved preferences - use OS preference for reduce motion
          const initialSettings = {
            ...defaultSettings,
            reduceMotion: prefersReducedMotion,
          };
          setSettings(initialSettings);
          applyAccessibilityClasses(initialSettings);
        }
      } catch (error) {
        console.error('Error loading accessibility settings:', error);
        const fallbackSettings = {
          ...defaultSettings,
          reduceMotion: prefersReducedMotion,
        };
        setSettings(fallbackSettings);
        applyAccessibilityClasses(fallbackSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user, applyAccessibilityClasses]);

  // Listen for OS reduced motion preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-update if user hasn't set a preference
      if (!user) {
        setSettings(prev => {
          const newSettings = { ...prev, reduceMotion: e.matches };
          applyAccessibilityClasses(newSettings);
          return newSettings;
        });
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [user, applyAccessibilityClasses]);

  const updateSetting = useCallback(async (key: keyof AccessibilitySettings, value: boolean) => {
    if (!user) return;

    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applyAccessibilityClasses(newSettings);

    // Map camelCase to snake_case for database
    const dbKeyMap: Record<keyof AccessibilitySettings, string> = {
      reduceMotion: 'reduce_motion',
      highContrast: 'high_contrast',
      largerText: 'larger_text',
      showMotionLabels: 'show_motion_labels',
    };

    try {
      // Upsert with all fields to ensure INSERT has complete data
      const { error } = await supabase
        .from('accessibility_preferences')
        .upsert({
          user_id: user.id,
          reduce_motion: newSettings.reduceMotion,
          high_contrast: newSettings.highContrast,
          larger_text: newSettings.largerText,
          show_motion_labels: newSettings.showMotionLabels,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (error) {
        console.error('Error saving accessibility setting:', error);
        // Revert on error
        setSettings(settings);
        applyAccessibilityClasses(settings);
      }
    } catch (error) {
      console.error('Error saving accessibility setting:', error);
      // Revert on error
      setSettings(settings);
      applyAccessibilityClasses(settings);
    }
  }, [user, settings, applyAccessibilityClasses]);

  return (
    <AccessibilityContext.Provider value={{ settings, loading, updateSetting }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}
